import React, {useMemo} from 'react';
import useMediaQuery from "@mui/system/useMediaQuery";
import {Typography, useTheme} from "@mui/joy";
import Dialog from "@mui/material/Dialog";
import {BasicModalContent} from "@dreebit-registry/vsm-microservice-components";
import {resolveErrorMessage} from "../../utils/resolveErrorMessage";
import {useTranslation} from "react-i18next";
import {useMutation} from "@apollo/client";
import {toast} from "react-toastify";
import moment from "moment";
import InstalledBaseTDAForm from "../InstalledBaseTDAForm";
import {AcknowledgeIotAlert} from "../../graphql/mutations/acknowledgeIotAlert";

interface AlertTarget {
    id: string;
    title?: string | null;
}

interface Props {
    open: boolean;
    alert: AlertTarget | null;
    onClose: () => void;
}

const AcknowledgeIotAlertModal = ({open, alert, onClose}: Props) => {
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const {t} = useTranslation();

    const [acknowledgeMutation] = useMutation(AcknowledgeIotAlert);

    const today = useMemo(() => moment().format('YYYY-MM-DD'), []);

    const attributes = useMemo(() => ([
        {
            index: 'muteFrom',
            name: t('Mute from'),
            type: 'date',
            inputType: 'date',
            disabled: false,
            value: today,
            categories: null,
            dynamicFilterListKey: null,
            list: false,
            hint: null,
            dataLoaderConfig: null,
            parentAttributeIndex: null,
            optionValues: null,
            rules: [{name: 'required', message: t('Mute from is required')}],
            addOnAttribute: null,
            subAttributes: null,
        },
        {
            index: 'muteUntil',
            name: t('Mute until'),
            type: 'date',
            inputType: 'date',
            disabled: false,
            value: null,
            categories: null,
            dynamicFilterListKey: null,
            list: false,
            hint: t('The alert will be muted until this date'),
            dataLoaderConfig: null,
            parentAttributeIndex: null,
            optionValues: null,
            rules: [{name: 'required', message: t('Mute until is required')}],
            addOnAttribute: null,
            subAttributes: null,
        },
        {
            index: 'recommendation',
            name: t('Recommendation'),
            type: 'string',
            inputType: 'textarea',
            disabled: false,
            value: '',
            categories: null,
            dynamicFilterListKey: null,
            list: false,
            hint: null,
            dataLoaderConfig: null,
            parentAttributeIndex: null,
            optionValues: null,
            rules: [],
            addOnAttribute: null,
            subAttributes: null,
        },
    ]), [t, today]);

    const handleSubmit = (values: {[k: string]: any}) => {
        if (!alert) return;

        const muteFrom = moment(values.muteFrom);
        const muteUntil = moment(values.muteUntil);
        const startOfToday = moment().startOf('day');

        if (muteFrom.isBefore(startOfToday)) {
            toast.error(t('Mute from cannot be in the past'));
            return;
        }

        if (!muteUntil.isAfter(startOfToday) || !muteUntil.isAfter(muteFrom)) {
            toast.error(t('Mute until must be after the start date and after today'));
            return;
        }

        return acknowledgeMutation({
            variables: {
                id: alert.id,
                input: {
                    muteFrom: muteFrom.toISOString(),
                    muteUntil: muteUntil.toISOString(),
                    recommendation: values.recommendation || undefined,
                },
            },
            refetchQueries: ["AssetIotAlertList"],
        })
            .then(() => {
                toast.success(t('Alert acknowledged'));
                onClose();
            })
            .catch((err) => {
                toast.error(resolveErrorMessage(err, t));
            });
    };

    return (
        <Dialog open={open} onClose={onClose} fullScreen={isFullScreen} fullWidth maxWidth="sm">
            <BasicModalContent
                onClose={onClose}
                title={<Typography level={'titleSmallLight'}>{t('Acknowledge alert')}</Typography>}
            >
                <InstalledBaseTDAForm
                    formStyle={{paddingTop: '0px'}}
                    attributes={attributes}
                    onSubmit={handleSubmit}
                    onCancel={onClose}
                    submitButtonText={t('Acknowledge')}
                />
            </BasicModalContent>
        </Dialog>
    );
};

export default AcknowledgeIotAlertModal;
